/**
 * Reset Password Page (Gate F9)
 * 
 * Allows users to set a new password using a reset token from email.
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { Lock, Loader2, ArrowLeft, CheckCircle, AlertTriangle } from "lucide-react";

import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { AppLogo, AppBrandingText } from "@/components/app-logo";
import { PasswordRequirementsChecklist, PasswordStrengthIndicator } from "@/components/password-requirements";
import { validatePassword } from "@/lib/password-validation";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/hooks/use-page-title";

const resetPasswordSchema = z.object({
  newPassword: z.string().min(1, "Password is required"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => validatePassword(data.newPassword).isValid, {
  message: "Password does not meet requirements",
  path: ["newPassword"],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

interface TokenVerifyResponse {
  valid: boolean;
  email?: string;
  message?: string;
}

export default function ResetPasswordPage() {
  usePageTitle("Reset Password");
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const search = useSearch();
  const token = new URLSearchParams(search).get("token") || "";
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const watchPassword = form.watch("newPassword");

  const { data: tokenStatus, isLoading: isVerifying, isError: isTokenError } = useQuery<TokenVerifyResponse>({
    queryKey: ["/api/auth/password-reset/verify", token],
    queryFn: async () => {
      const response = await fetch(`/api/auth/password-reset/verify?token=${encodeURIComponent(token)}`);
      const data = await response.json();
      if (!response.ok) {
        setTokenError(data.message || "Invalid or expired reset link");
        throw new Error(data.message || "Invalid token");
      }
      return { ...data, valid: true };
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  const resetMutation = useMutation({
    mutationFn: async (data: ResetPasswordFormData) => {
      return apiRequest("POST", "/api/auth/password-reset/confirm", {
        token,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });
    },
    onSuccess: () => {
      setIsSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.message || "";
      
      if (errorMessage.includes("TOKEN_EXPIRED")) {
        toast({
          title: "Link expired",
          description: "This reset link has expired. Please request a new one.",
          variant: "destructive",
        });
      } else if (errorMessage.includes("TOKEN_USED")) {
        toast({
          title: "Link already used",
          description: "This reset link has already been used. Please request a new one.",
          variant: "destructive",
        });
      } else if (errorMessage.includes("PASSWORD_TOO_WEAK")) {
        form.setError("newPassword", { message: "Password does not meet requirements" });
      } else if (errorMessage.includes("PASSWORD_MISMATCH")) {
        form.setError("confirmPassword", { message: "Passwords don't match" });
      } else {
        toast({
          title: "Error",
          description: "Failed to reset password. Please try again.",
          variant: "destructive",
        });
      }
    },
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    resetMutation.mutate(data);
  };

  const getErrorMessage = () => {
    if (tokenError) return tokenError;
    return "This password reset link is invalid.";
  };

  const isTokenInvalid = !token || isTokenError || (tokenStatus && !tokenStatus.valid);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <AppLogo size="2xl" />
          </div>
          <h1 className="text-3xl" data-testid="text-page-title">
            <AppBrandingText />
          </h1>
          <p className="text-muted-foreground mt-2">Behavior Evals, Tracking, & Insights</p>
        </div>

        <Card>
          {isVerifying ? (
            <>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-60" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </>
          ) : isSuccess ? (
            <>
              <CardHeader>
                <CardTitle>Password Reset</CardTitle>
                <CardDescription>
                  Your password has been reset successfully.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-center py-4">
                    <CheckCircle className="h-16 w-16 text-green-500" />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Redirecting you to login in 3 seconds...
                  </p>
                  <Link href="/login">
                    <Button className="w-full" data-testid="button-continue-login">
                      Continue to Login
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </>
          ) : isTokenInvalid ? (
            <>
              <CardHeader>
                <CardTitle>Invalid Link</CardTitle>
                <CardDescription>
                  {getErrorMessage()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-center py-4">
                    <AlertTriangle className="h-16 w-16 text-yellow-500" />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    Password reset links are only valid for 1 hour.
                    Please request a new one if you need to reset your password.
                  </p>
                  <Link href="/forgot-password">
                    <Button className="w-full" data-testid="button-request-new-reset">
                      Request a New Reset Link
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </>
          ) : tokenStatus ? (
            <>
              <CardHeader>
                <CardTitle>Set New Password</CardTitle>
                <CardDescription>
                  {tokenStatus.email && (
                    <>Resetting password for <strong>{tokenStatus.email}</strong></>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                {...field}
                                type="password"
                                placeholder="Enter new password"
                                className="pl-10"
                                data-testid="input-new-password"
                                disabled={resetMutation.isPending}
                              />
                            </div>
                          </FormControl>
                          <PasswordStrengthIndicator password={field.value} />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="bg-muted/50 rounded-lg p-3">
                      <PasswordRequirementsChecklist password={watchPassword} />
                    </div>

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                {...field}
                                type="password"
                                placeholder="Confirm new password"
                                className="pl-10"
                                data-testid="input-confirm-password"
                                disabled={resetMutation.isPending}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={
                        resetMutation.isPending ||
                        !validatePassword(watchPassword).isValid ||
                        watchPassword !== form.watch("confirmPassword")
                      }
                      data-testid="button-reset-password"
                    >
                      {resetMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="mt-6 pt-6 border-t text-center">
                  <Link href="/login" className="text-sm text-muted-foreground hover:text-primary">
                    <ArrowLeft className="inline-block mr-1 h-3 w-3" />
                    Back to Login
                  </Link>
                </div>
              </CardContent>
            </>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
